import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

type SubType = 'contact' | 'bank';

interface Contact {
  id: string;
  fullName: string;
  position: string | null;
  email: string | null;
  phone: string | null;
  contactType: string;
  isPrimary: boolean;
}
interface BankAccount {
  id: string;
  bankName: string;
  iban: string;
  currency: string;
  mfo: string | null;
  accountHolder: string | null;
  isPrimary: boolean;
}
type SubItem = Contact | BankAccount;

interface LegalProfile {
  fullLegalName?: string;
  legalAddress?: string;
  actualAddress?: string;
  registrationCountry?: string;
  vatStatus?: boolean | string;
  vatNumber?: string;
  directorName?: string;
  authorizedPerson?: string;
  basisOfAuthority?: string;
}

function useFormColors(isDark: boolean) {
  return {
    cardBg: isDark ? '#1A1A1A' : '#fff',
    cardBorder: isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8',
    textMain: isDark ? '#E0E0E0' : '#1A1A1A',
    textMuted: isDark ? '#8E8E93' : '#6B6B70',
    mutedText: isDark ? '#6B6B70' : '#9B9BA4',
    inputBg: isDark ? '#1A1A1A' : '#fff',
    inputBorder: isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8',
    tableHeaderBg: isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB',
    tableBorder: isDark ? 'rgba(255,255,255,0.05)' : '#F0F0F2',
    footerBg: isDark ? 'rgba(255,255,255,0.03)' : '#FAFAFA',
    footerBorder: isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8',
    cancelBg: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
    modalOverlay: 'rgba(0,0,0,0.5)',
  };
}

// ============================================================================
// Legal Profile editable form
// ============================================================================

export function LegalProfileForm({ counterpartyId, initial, onSaved }: {
  counterpartyId: string;
  initial: LegalProfile | undefined;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<LegalProfile>(initial || {});
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const clr = useFormColors(isDark);

  useEffect(() => { setForm(initial || {}); }, [initial]);

  const set = (k: keyof LegalProfile, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload: any = { ...form };
      if (typeof payload.vatStatus === 'string') {
        payload.vatStatus = payload.vatStatus === 'true' || payload.vatStatus === 'Yes';
      }
      await api.patch(`/counterparties/${counterpartyId}/legal-profile`, payload);
      addToast('Legal profile saved', 'success');
      onSaved();
    } catch (e) {
      addToast(getApiErrorMessage(e, 'Save failed'), 'error');
    } finally { setSaving(false); }
  };

  const F = (label: string, key: keyof LegalProfile, wide?: boolean, placeholder?: string) => (
    <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-[11px] mb-1 block" style={{ color: clr.textMuted }}>{label}</span>
      <input
        type="text"
        value={(form[key] as any) ?? ''}
        onChange={(e) => set(key, e.target.value)}
        placeholder={placeholder}
        className="w-full text-xs px-3 py-2 rounded-md outline-none"
        style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}
      />
    </label>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Full legal name *', 'fullLegalName', true)}
        {F('Legal address', 'legalAddress', true)}
        {F('Actual address', 'actualAddress', true)}
        {F('Registration country', 'registrationCountry', false, 'Ukraine')}
        <label className="block">
          <span className="text-[11px] mb-1 block" style={{ color: clr.textMuted }}>VAT status</span>
          <select
            value={form.vatStatus === true || form.vatStatus === 'true' ? 'true' : 'false'}
            onChange={(e) => set('vatStatus', e.target.value)}
            className="w-full text-xs px-3 py-2 rounded-md outline-none"
            style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}
          >
            <option value="false">Non-VAT payer</option>
            <option value="true">VAT payer</option>
          </select>
        </label>
        {F('VAT number', 'vatNumber')}
        {F('Director name', 'directorName')}
        {F('Authorized person', 'authorizedPerson')}
        {F('Basis of authority', 'basisOfAuthority', true, 'Statute / Power of Attorney №...')}
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="text-xs px-4 py-2 rounded-md cursor-pointer font-medium" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
          {saving ? t('action_saving') : t('action_save')}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Contacts / Bank Accounts list+modal CRUD section
// ============================================================================

const SUB_META: Record<SubType, { label: string; endpoint: string; required: string[] }> = {
  contact: { label: 'contact', endpoint: 'contacts', required: ['fullName'] },
  bank: { label: 'bank account', endpoint: 'bank-accounts', required: ['bankName', 'iban'] },
};

export function CounterpartySubResourceSection({ type, counterpartyId, items, onChange }: {
  type: SubType;
  counterpartyId: string;
  items: SubItem[];
  onChange: () => void;
}) {
  const meta = SUB_META[type];
  const [editing, setEditing] = useState<SubItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const clr = useFormColors(isDark);

  const handleAdd = () => { setEditing(null); setModalOpen(true); };
  const handleEdit = (it: SubItem) => { setEditing(it); setModalOpen(true); };
  const handleDelete = async (it: SubItem) => {
    const label = type === 'contact' ? (it as Contact).fullName : (it as BankAccount).bankName;
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/counterparties/${counterpartyId}/${meta.endpoint}/${it.id}`);
      addToast('Deleted', 'success');
      onChange();
    } catch (e) {
      addToast(getApiErrorMessage(e, 'Delete failed'), 'error');
    }
  };
  const handleSaved = () => { setModalOpen(false); onChange(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs" style={{ color: clr.textMuted }}>{items.length} {meta.label}{items.length === 1 ? '' : 's'}</span>
        <button onClick={handleAdd} className="text-xs font-medium px-3 py-1.5 rounded-md cursor-pointer whitespace-nowrap" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
          <i className="ri-add-line text-xs mr-1"></i>Add {meta.label}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-6 text-xs" style={{ color: clr.mutedText }}>No {meta.label}s yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: clr.tableHeaderBg, color: clr.textMuted }}>
                {type === 'contact' && (
                  <>
                    <th className="text-left px-3 py-2 font-medium">Full name</th>
                    <th className="text-left px-3 py-2 font-medium">Position</th>
                    <th className="text-left px-3 py-2 font-medium">Email</th>
                    <th className="text-left px-3 py-2 font-medium">Phone</th>
                    <th className="text-left px-3 py-2 font-medium">Type</th>
                    <th className="text-center px-3 py-2 font-medium">Primary</th>
                  </>
                )}
                {type === 'bank' && (
                  <>
                    <th className="text-left px-3 py-2 font-medium">Bank</th>
                    <th className="text-left px-3 py-2 font-medium" style={{ fontFamily: 'monospace' }}>IBAN</th>
                    <th className="text-left px-3 py-2 font-medium">Currency</th>
                    <th className="text-left px-3 py-2 font-medium">MFO</th>
                    <th className="text-left px-3 py-2 font-medium">Holder</th>
                    <th className="text-center px-3 py-2 font-medium">Primary</th>
                  </>
                )}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} style={{ borderTop: `1px solid ${clr.tableBorder}` }}>
                  {type === 'contact' && (
                    <>
                      <td className="px-3 py-2" style={{ color: clr.textMain }}>{(it as Contact).fullName}</td>
                      <td className="px-3 py-2" style={{ color: clr.textMuted }}>{(it as Contact).position || '—'}</td>
                      <td className="px-3 py-2" style={{ color: clr.textMuted }}>{(it as Contact).email || '—'}</td>
                      <td className="px-3 py-2" style={{ color: clr.textMuted }}>{(it as Contact).phone || '—'}</td>
                      <td className="px-3 py-2 capitalize" style={{ color: clr.textMuted }}>{(it as Contact).contactType}</td>
                    </>
                  )}
                  {type === 'bank' && (
                    <>
                      <td className="px-3 py-2" style={{ color: clr.textMain }}>{(it as BankAccount).bankName}</td>
                      <td className="px-3 py-2" style={{ color: clr.textMain, fontFamily: 'monospace' }}>{(it as BankAccount).iban}</td>
                      <td className="px-3 py-2" style={{ color: clr.textMuted }}>{(it as BankAccount).currency}</td>
                      <td className="px-3 py-2" style={{ color: clr.textMuted }}>{(it as BankAccount).mfo || '—'}</td>
                      <td className="px-3 py-2" style={{ color: clr.textMuted }}>{(it as BankAccount).accountHolder || '—'}</td>
                    </>
                  )}
                  <td className="px-3 py-2 text-center">
                    {(it as any).isPrimary ? <i className="ri-checkbox-circle-fill text-sm" style={{ color: '#0D7A3C' }}></i> : <span style={{ color: clr.mutedText }}>—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => handleEdit(it)} className="text-xs px-2 py-1 rounded cursor-pointer" style={{ color: '#1F5B8F' }}>
                      <i className="ri-edit-line text-xs mr-0.5"></i>Edit
                    </button>
                    <button onClick={() => handleDelete(it)} className="text-xs px-2 py-1 rounded cursor-pointer ml-1" style={{ color: 'var(--accent)' }}>
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <SubItemModal type={type} counterpartyId={counterpartyId} endpoint={meta.endpoint} item={editing} onClose={() => setModalOpen(false)} onSaved={handleSaved} />
      )}
    </div>
  );
}

function SubItemModal({ type, counterpartyId, endpoint, item, onClose, onSaved }: {
  type: SubType;
  counterpartyId: string;
  endpoint: string;
  item: SubItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { isDark } = useTheme();
  const clr = useFormColors(isDark);
  const isEdit = !!item;
  const [form, setForm] = useState<Record<string, any>>(() => {
    if (item) return { ...item };
    return type === 'contact'
      ? { fullName: '', position: '', email: '', phone: '', contactType: 'general', isPrimary: false }
      : { bankName: '', iban: '', currency: 'UAH', mfo: '', accountHolder: '', isPrimary: false };
  });
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();
  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (addAnother: boolean = false) => {
    setSaving(true);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (k === 'isPrimary') payload[k] = !!v;
        else payload[k] = v === '' ? null : v;
      }
      if (isEdit) {
        await api.patch(`/counterparties/${counterpartyId}/${endpoint}/${item!.id}`, payload);
        addToast('Saved', 'success');
        onSaved();
      } else {
        await api.post(`/counterparties/${counterpartyId}/${endpoint}`, payload);
        addToast('Added', 'success');
        if (addAnother) {
          setForm(type === 'contact'
            ? { fullName: '', position: '', email: '', phone: '', contactType: 'general', isPrimary: false }
            : { bankName: '', iban: '', currency: form.currency || 'UAH', mfo: '', accountHolder: '', isPrimary: false });
        } else { onSaved(); }
      }
    } catch (e) {
      addToast(getApiErrorMessage(e, 'Save failed'), 'error');
    } finally { setSaving(false); }
  };

  const F = (label: string, key: string, opts: { wide?: boolean; placeholder?: string; type?: string } = {}) => (
    <label className={`block ${opts.wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-[11px] mb-1 block" style={{ color: clr.textMuted }}>{label}</span>
      <input
        type={opts.type || 'text'}
        value={form[key] ?? ''}
        onChange={(e) => set(key, e.target.value)}
        placeholder={opts.placeholder}
        className="w-full text-xs px-3 py-2 rounded-md outline-none"
        style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}
      />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: clr.modalOverlay }} onClick={onClose}>
      <div className="rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ background: clr.cardBg }} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${clr.cardBorder}` }}>
          <h3 className="text-base font-semibold" style={{ color: clr.textMain }}>
            {isEdit ? 'Edit' : 'Add'} {type === 'contact' ? 'contact' : 'bank account'}
          </h3>
          <button onClick={onClose} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
            <i className="ri-close-line" style={{ color: clr.textMuted }}></i>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {type === 'contact' && (
              <>
                {F('Full name *', 'fullName', { wide: true })}
                {F('Position', 'position')}
                <label className="block">
                  <span className="text-[11px] mb-1 block" style={{ color: clr.textMuted }}>Contact type</span>
                  <select value={form.contactType} onChange={(e) => set('contactType', e.target.value)} className="w-full text-xs px-3 py-2 rounded-md outline-none" style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}>
                    <option value="general">General</option>
                    <option value="technical">Technical</option>
                    <option value="financial">Financial</option>
                    <option value="legal">Legal</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </label>
                {F('Email', 'email', { type: 'email' })}
                {F('Phone', 'phone', { placeholder: '+380...' })}
              </>
            )}
            {type === 'bank' && (
              <>
                {F('Bank name *', 'bankName', { wide: true })}
                {F('IBAN *', 'iban', { wide: true, placeholder: 'UA00...' })}
                {F('Currency', 'currency', { placeholder: 'UAH / EUR / USD' })}
                {F('MFO', 'mfo')}
                {F('Account holder', 'accountHolder', { wide: true })}
              </>
            )}
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!form.isPrimary} onChange={(e) => set('isPrimary', e.target.checked)} />
            <span className="text-xs" style={{ color: clr.textMain }}>Mark as primary {type}</span>
          </label>
        </div>

        <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: `1px solid ${clr.footerBorder}`, background: clr.footerBg }}>
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-md cursor-pointer" style={{ background: clr.cancelBg, border: `1px solid ${clr.cardBorder}`, color: clr.textMain }}>Cancel</button>
          {!isEdit && (
            <button onClick={() => save(true)} disabled={saving} className="text-xs px-4 py-2 rounded-md cursor-pointer" style={{ background: clr.cancelBg, border: '1px solid var(--accent)', color: 'var(--accent)' }}>
              Save & add another
            </button>
          )}
          <button onClick={() => save(false)} disabled={saving} className="text-xs px-4 py-2 rounded-md cursor-pointer font-medium" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
            {saving ? 'Saving...' : (isEdit ? 'Save' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Core info inline edit (name, shortName, edrpou, taxId, country, status)
// ============================================================================

interface CoreFields {
  name: string;
  shortName?: string;
  edrpou?: string;
  taxId?: string;
  country?: string;
  status?: string;
}

export function CounterpartyCoreInfoEditor({ counterpartyId, initial, onSaved }: {
  counterpartyId: string;
  initial: CoreFields;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<CoreFields>(initial);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const clr = useFormColors(isDark);

  useEffect(() => { setForm(initial); }, [initial]);
  const set = (k: keyof CoreFields, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload: any = {};
      for (const [k, v] of Object.entries(form)) { payload[k] = v === '' ? null : v; }
      await api.patch(`/counterparties/${counterpartyId}`, payload);
      addToast('Saved', 'success');
      onSaved();
    } catch (e) {
      addToast(getApiErrorMessage(e, 'Save failed'), 'error');
    } finally { setSaving(false); }
  };

  const F = (label: string, key: keyof CoreFields, opts: { wide?: boolean; placeholder?: string } = {}) => (
    <label className={`block ${opts.wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-[11px] mb-1 block" style={{ color: clr.textMuted }}>{label}</span>
      <input
        type="text"
        value={(form[key] as any) ?? ''}
        onChange={(e) => set(key, e.target.value)}
        placeholder={opts.placeholder}
        className="w-full text-xs px-3 py-2 rounded-md outline-none"
        style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}
      />
    </label>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Counterparty name *', 'name', { wide: true })}
        {F('Short name', 'shortName')}
        {F('EDRPOU', 'edrpou')}
        {F('Tax ID', 'taxId')}
        {F('Country', 'country')}
        <label className="block">
          <span className="text-[11px] mb-1 block" style={{ color: clr.textMuted }}>Status</span>
          <select value={form.status || ''} onChange={(e) => set('status', e.target.value)} className="w-full text-xs px-3 py-2 rounded-md outline-none" style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}>
            <option value="draft">draft</option>
            <option value="active">active</option>
            <option value="pending">pending</option>
            <option value="suspended">suspended</option>
          </select>
        </label>
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="text-xs px-4 py-2 rounded-md cursor-pointer font-medium" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
          {saving ? t('action_saving') : t('action_save')}
        </button>
      </div>
    </div>
  );
}